import type { Ambience, Ensemble, Feel, MusicTags } from "../shared/story";
import * as I from "./instruments";

/**
 * The score: generative music under the story, played by the instruments in instruments.ts. What plays follows the
 * story three ways:
 * - the scene's tags (the canon pass picks an ensemble and a feel per scene: solo cello and melancholy, guitar and bass
 *   and playful, brass and ominous...): the instruments, the modes, the tempo and the phrasing;
 * - its tension (1..5) and crisis: how busy it is (sparse phrases and slow chords when calm, a pulse as it builds,
 *   quicker and darker at a peak, thinning as it fades); a sudden event rings once;
 * - the setting: its key, and the ensemble and feel to use before the story has chosen.
 * Now and then a guest joins for a few bars (a flute's counter-line, a harp glissando, a far tubular bell), so the
 * same scene never sounds the same for long. Changes land on the next chord, and moods drift over half a minute.
 *
 * Silence is part of it. The score moves in sections of a few bars: full; sparse (one quiet voice alone: a violin or
 * cello on a slow line that barely moves, a guitar's soft chord once a bar, single piano or harp notes); or silent
 * (nothing at all). Calm stretches breathe often, building ones less, a peak or a crisis never. The story can ask for
 * silence outright (the "silence" ensemble).
 *
 * Pure scheduling on any BaseAudioContext: `schedule(until)` writes every note up to that time, so the same code plays
 * live (a timer keeps it a second ahead) and renders offline (for listening tests).
 */

type Ctx = BaseAudioContext;
type Key = Ambience;

const MODES = {
  ionian: [0, 2, 4, 5, 7, 9, 11], lydian: [0, 2, 4, 6, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10], aeolian: [0, 2, 3, 5, 7, 8, 10], phrygian: [0, 1, 3, 5, 7, 8, 10],
} as const;
type Mode = keyof typeof MODES;

// ---- settings, feels, ensembles ---------------------------------------------------------------------------------------

interface Setting { root: number; ensemble: Ensemble; feel: Feel; pulse: number; bright: number }
/** null: the setting is music already (the jazz club), so the score rests. */
const SETTINGS: Record<Key, Setting | null> = {
  silence: { root: 48, ensemble: "felt piano", feel: "tender", pulse: 0, bright: 0.5 },
  rain: { root: 50, ensemble: "felt piano", feel: "melancholy", pulse: 0, bright: 0.35 },
  wind: { root: 43, ensemble: "solo cello", feel: "mysterious", pulse: 0, bright: 0.3 },
  sea: { root: 45, ensemble: "harp", feel: "serene", pulse: 0, bright: 0.55 },
  train: { root: 48, ensemble: "acoustic guitar", feel: "serene", pulse: 0.3, bright: 0.45 },
  city: { root: 41, ensemble: "electric piano", feel: "mysterious", pulse: 0.25, bright: 0.5 },
  cafe: { root: 43, ensemble: "guitar and bass", feel: "playful", pulse: 0.2, bright: 0.6 },
  jazz: null,
  fire: { root: 46, ensemble: "solo cello", feel: "tender", pulse: 0, bright: 0.4 },
  night: { root: 52, ensemble: "woodwinds", feel: "wonder", pulse: 0, bright: 0.5 },
  house: { root: 49, ensemble: "music box", feel: "eerie", pulse: 0, bright: 0.3 },
  clock: { root: 47, ensemble: "mallets", feel: "mysterious", pulse: 0.35, bright: 0.4 },
  crowd: { root: 51, ensemble: "string quartet", feel: "mysterious", pulse: 0.2, bright: 0.5 },
  water: { root: 42, ensemble: "bells", feel: "wonder", pulse: 0, bright: 0.6 },
};

interface FeelSpec { modes: [Mode, Mode, Mode]; tempo: number; legato: number; dark: number }
/** A feel: its modes when calm / building / at a peak, a tempo lean (bpm), how connected the phrases are, and how dark. */
const FEELS: Record<Feel, FeelSpec> = {
  tender: { modes: ["ionian", "lydian", "aeolian"], tempo: -6, legato: 0.95, dark: 0 },
  serene: { modes: ["lydian", "lydian", "dorian"], tempo: -8, legato: 1, dark: 0 },
  wonder: { modes: ["lydian", "mixolydian", "dorian"], tempo: -2, legato: 0.9, dark: 0 },
  melancholy: { modes: ["aeolian", "dorian", "phrygian"], tempo: -6, legato: 1, dark: 0.3 },
  mysterious: { modes: ["dorian", "aeolian", "phrygian"], tempo: -3, legato: 0.85, dark: 0.3 },
  playful: { modes: ["mixolydian", "ionian", "dorian"], tempo: 10, legato: 0.55, dark: 0 },
  romantic: { modes: ["ionian", "mixolydian", "aeolian"], tempo: -4, legato: 1, dark: 0 },
  tense: { modes: ["aeolian", "phrygian", "phrygian"], tempo: 4, legato: 0.75, dark: 0.6 },
  ominous: { modes: ["phrygian", "phrygian", "aeolian"], tempo: -2, legato: 0.9, dark: 0.8 },
  urgent: { modes: ["aeolian", "aeolian", "phrygian"], tempo: 14, legato: 0.6, dark: 0.5 },
  triumphant: { modes: ["ionian", "mixolydian", "mixolydian"], tempo: 6, legato: 0.85, dark: 0 },
  eerie: { modes: ["lydian", "phrygian", "phrygian"], tempo: -6, legato: 0.9, dark: 0.6 },
};

type Lead = "piano" | "cello" | "violin" | "flute" | "clarinet" | "horn" | "harp" | "celesta" | "musicbox" | "vibes" | "ep";
type Harmony = "pad" | "strings" | "choir" | "pick" | "strum" | "harp" | "piano" | "ep" | "vibes" | "marimba" | "horns" | "celesta";
type Bass = "sub" | "cello" | "bass" | "none";
type Pulse = "pizz" | "marimba" | "glock" | "piano" | "ep" | null;
type Guest = "flute" | "clarinet" | "violin" | "cello" | "glock" | "celesta" | "handbell" | "harp" | "tubular" | "choir" | "vibes";

/** trim: loudness, measured offline so every ensemble sits at about the same level. solo: the bowed strings are single
 * players, not sections (only matters with recorded instruments: the intimate ensembles). */
interface Arrangement { lead: Lead | null; harmony: Harmony; bass: Bass; pulse: Pulse; guests: Guest[]; trim: number; solo?: boolean }
/** The arrangements, for the lab's offline renders only (packages/lab/loom/score: one-off variations for demos). */
export { ARR as LAB_ARRANGEMENTS };
const ARR: Record<Ensemble, Arrangement> = {
  silence: { lead: null, harmony: "pad", bass: "none", pulse: null, guests: [], trim: 1 },
  "felt piano": { lead: "piano", harmony: "piano", bass: "sub", pulse: "piano", guests: ["cello", "flute", "celesta", "violin"], trim: 1.0, solo: true },
  "solo cello": { lead: "cello", harmony: "pad", bass: "sub", pulse: "pizz", guests: ["harp", "handbell", "tubular", "flute"], trim: 1.0, solo: true },
  "solo violin": { lead: "violin", harmony: "harp", bass: "cello", pulse: "pizz", guests: ["cello", "celesta", "flute"], trim: 1.4, solo: true },
  "string quartet": { lead: "violin", harmony: "strings", bass: "cello", pulse: "pizz", guests: ["harp", "tubular", "flute"], trim: 1.55 },
  "acoustic guitar": { lead: null, harmony: "pick", bass: "bass", pulse: null, guests: ["flute", "cello", "glock", "violin"], trim: 2.6, solo: true },
  "guitar and bass": { lead: null, harmony: "strum", bass: "bass", pulse: null, guests: ["clarinet", "vibes", "flute"], trim: 2.0 },
  woodwinds: { lead: "flute", harmony: "pad", bass: "sub", pulse: "marimba", guests: ["clarinet", "harp", "glock", "clarinet"], trim: 0.95 },
  harp: { lead: "harp", harmony: "harp", bass: "sub", pulse: null, guests: ["flute", "celesta", "violin", "cello"], trim: 0.95, solo: true },
  bells: { lead: "celesta", harmony: "pad", bass: "sub", pulse: "glock", guests: ["tubular", "handbell", "harp", "choir"], trim: 0.95 },
  mallets: { lead: "vibes", harmony: "marimba", bass: "bass", pulse: null, guests: ["flute", "clarinet", "glock"], trim: 1.9 },
  choir: { lead: null, harmony: "choir", bass: "sub", pulse: null, guests: ["tubular", "celesta", "cello", "handbell"], trim: 1.0 },
  "music box": { lead: "musicbox", harmony: "pad", bass: "none", pulse: null, guests: ["celesta", "violin", "choir"], trim: 2.0 },
  brass: { lead: "horn", harmony: "horns", bass: "cello", pulse: "pizz", guests: ["tubular", "choir", "violin"], trim: 1.2 },
  "electric piano": { lead: "ep", harmony: "ep", bass: "sub", pulse: "ep", guests: ["vibes", "flute", "glock"], trim: 1.15 },
};

/**
 * The recorded sets (sampler.ts) an ensemble can play: its lead, harmony, bass, pulse and guests, and the bells a
 * sudden event rings. What the score preloads when the ensemble changes; everything else stays synthesized.
 */
export function samplesFor(ens: Ensemble): string[] {
  const a = ARR[ens];
  const bowed = (k: "cello" | "violin") => (a.solo ? `${k}.solo` : `${k}.sustain`);
  const LEAD: Partial<Record<Lead, string>> = { piano: "piano.soft", cello: bowed("cello"), violin: bowed("violin"), flute: "flute.sustain", clarinet: "clarinet.sustain", horn: "horn.sustain", harp: "harp.pluck", vibes: "vibraphone.hit" };
  const HARMONY: Partial<Record<Harmony, string[]>> = {
    strings: ["cello.sustain", "viola.sustain", "cello.tremolo", "viola.tremolo"], pick: ["guitar.pluck"], strum: ["guitar.pluck"], harp: ["harp.pluck"],
    piano: ["piano.soft"], vibes: ["vibraphone.hit"], marimba: ["marimba.hit"], horns: ["horn.sustain"],
  };
  const BASS: Partial<Record<Bass, string[]>> = { cello: [bowed("cello"), "cello.tremolo"], bass: ["bass.pluck"] };
  const PULSE: Partial<Record<NonNullable<Pulse>, string>> = { pizz: "strings.pizz", marimba: "marimba.hit", glock: "glockenspiel.hit", piano: "piano.soft" };
  const GUEST: Partial<Record<Guest, string>> = { flute: "flute.sustain", clarinet: "clarinet.sustain", violin: bowed("violin"), cello: bowed("cello"), glock: "glockenspiel.hit", harp: "harp.pluck", tubular: "tubular.hit", vibes: "vibraphone.hit" };
  const out = [
    ...(a.lead && LEAD[a.lead] ? [LEAD[a.lead]!] : []), ...(HARMONY[a.harmony] ?? []), ...(BASS[a.bass] ?? []),
    ...(a.pulse && PULSE[a.pulse] ? [PULSE[a.pulse]!] : []), ...a.guests.flatMap((g) => (GUEST[g] ? [GUEST[g]!] : [])),
    "tubular.hit", "glockenspiel.hit",
  ];
  return ens === "silence" ? [] : [...new Set(out)];
}

/** Where each lead sings (MIDI centre). */
const CENTRE: Record<Lead, number> = { piano: 69, cello: 55, violin: 76, flute: 79, clarinet: 63, horn: 60, harp: 72, celesta: 84, musicbox: 86, vibes: 72, ep: 69 };
/** Sustained leads hold their notes for the phrase; struck ones ring on their own. */
const SUSTAINED = new Set<Lead>(["cello", "violin", "flute", "clarinet", "horn"]);

/** The story's state the score follows: the turn's tension (1..5, as the director keeps it) and whether a crisis is on. */
export interface Mood { tension: number; crisis: boolean }
/** How busy the music is (0..1) for a mood: still below 1.5, at full stretch from 4.5. */
export const intensityOf = (m: Mood) => Math.max(0, Math.min(1, (m.tension - 1.5) / 3 + (m.crisis ? 0.15 : 0)));

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const pick = <T,>(xs: readonly T[], rnd: () => number) => xs[Math.floor(rnd() * xs.length)]!;
function weighted<T>(items: [T, number][], rnd: () => number): T {
  const total = items.reduce((s, [, w]) => s + w, 0);
  let r = rnd() * total;
  for (const [x, w] of items) { r -= w; if (r <= 0) return x; }
  return items[items.length - 1]![0];
}

// ---- harmony -------------------------------------------------------------------------------------------------------

interface Chord { deg: number; bass: number; tones: number[]; pcs: number[] }

/** Pitch classes (relative to the root) of the chord on scale degree `deg`: root, 3rd, 5th, 7th, 9th. */
function stack(mode: Mode, deg: number): number[] {
  const s = MODES[mode];
  return [0, 2, 4, 6, 8].map((k) => s[(deg + k) % 7]! + 12 * Math.floor((deg + k) / 7));
}
/** Degrees whose triad is major or minor (no diminished chords: they pull too hard for music meant to be lived in). */
const usable = (mode: Mode) => [0, 1, 2, 3, 4, 5, 6].filter((d) => { const c = stack(mode, d); return c[2]! - c[0]! === 7; });

function nextDegree(mode: Mode, from: number, dark: boolean, rnd: () => number): number {
  const ok = usable(mode).filter((d) => d !== from);
  const pref: Record<number, number> = dark ? { 0: 3, 5: 3, 6: 2.5, 3: 2, 1: 1.2, 2: 0.8, 4: 1 } : { 0: 2.5, 3: 3, 5: 2.5, 1: 2, 4: 1.5, 2: 1, 6: 0.8 };
  return weighted(ok.map((d) => [d, pref[d] ?? 1] as [number, number]), rnd);
}

/** Voices the upper tones near the last ones (smallest movement), inside a warm middle register. */
function voice(root: number, mode: Mode, deg: number, prev: number[], count: number): Chord {
  const pcs = stack(mode, deg);
  const want = [pcs[1]!, pcs[3]!, pcs[4]!, pcs[2]!].slice(0, count); // 3rd, 7th, 9th, then 5th
  const centre = prev.length ? prev.reduce((s, x) => s + x, 0) / prev.length : root + 19;
  const tones: number[] = [];
  for (const pc of want) {
    let best = 0, bestD = Infinity;
    for (let oct = -1; oct <= 3; oct++) {
      const p = root + pc + 12 * oct;
      if (p < root + 10 || p > root + 33) continue;
      const near = prev.length ? Math.min(...prev.map((q) => Math.abs(q - p))) : Math.abs(p - centre);
      const clash = tones.some((t) => Math.abs(t - p) < (p < 60 ? 4 : 2));
      const d = near + (clash ? 12 : 0);
      if (d < bestD) { bestD = d; best = p; }
    }
    tones.push(best);
  }
  tones.sort((a, b) => a - b);
  const bassNote = root + pcs[0]! - (pcs[0]! >= 7 ? 12 : 0);
  return { deg, bass: bassNote, tones, pcs: pcs.map((p) => p % 12) };
}

/** Chord tones from `lo` to `hi` (for arpeggios, strums and counter-lines). */
function spread(root: number, c: Chord, lo: number, hi: number, triad = false): number[] {
  const pcs = triad ? [c.pcs[0]!, c.pcs[1]!, c.pcs[2]!] : c.pcs;
  const out: number[] = [];
  for (let m = lo; m <= hi; m++) if (pcs.includes(((m - root) % 12 + 12) % 12)) out.push(m);
  return out.length ? out : [lo];
}

// ---- the score -----------------------------------------------------------------------------------------------------

/** Loudness of the whole score (measured offline so it sits under the 0.08-RMS soundscape loops). */
const LEVEL = 0.26;

export class Score {
  private bus: I.Bus;
  private setting: Setting = SETTINGS.silence!;
  private tags: MusicTags | null = null;
  private arr: Arrangement = ARR["felt piano"];
  private feel: FeelSpec = FEELS.tender;
  private pending: { setting?: Setting; tags?: MusicTags | null } | null = null;
  private resting = false;
  private e = 0.15;
  private target = 0.15;
  private t = 0;
  private step = 0;
  private bar = -1;
  private chord: Chord | null = null;
  private held: I.Held[] = [];
  private phrase: { notes: { at: number; d: number; len: number }[]; i: number } | null = null;
  private melodyDeg = -1000;
  private guest: { kind: Guest; until: number } | null = null;
  private pendingAccent = false;
  private running = false;
  /** Full, sparse (one quiet voice) or silent, until bar `until`. */
  private section: { kind: "full" | "sparse" | "silent"; until: number } = { kind: "full", until: 6 };
  /** Off for listening tests of one ensemble; on in the story. */
  sections = true;

  constructor(private ctx: Ctx, dest: AudioNode, private rnd: () => number = Math.random) {
    this.bus = I.buildBus(ctx, dest);
  }

  /** Starts loading the recorded sets this score's ensemble plays (again once the sampler is ready). */
  prefetch(): Promise<void> { return I.prefetch(this.ctx, samplesFor(this.tags?.ensemble ?? this.setting.ensemble)); }

  start(at: number): void {
    if (this.running) return;
    this.running = true;
    this.t = at; this.step = 0; this.bar = -1; this.chord = null;
    this.applyPending();
    void this.prefetch();
    this.section = { kind: "full", until: 6 };
    this.bus.out.gain.cancelScheduledValues(at);
    this.bus.out.gain.setValueAtTime(this.bus.out.gain.value, at);
    this.bus.out.gain.linearRampToValueAtTime(this.resting ? 0 : LEVEL * this.arr.trim, at + 6);
  }
  stop(at: number): void {
    if (!this.running) return;
    this.running = false;
    this.bus.out.gain.cancelScheduledValues(at);
    this.bus.out.gain.setValueAtTime(this.bus.out.gain.value, at);
    this.bus.out.gain.linearRampToValueAtTime(0, at + 4);
    this.held.forEach((h) => h.stop(at + 4));
    this.held = [];
    this.phrase = null;
  }
  get playing() { return this.running; }

  /** The scene's setting: its key (and its default ensemble and feel) take over at the next chord. The jazz club rests. */
  setScene(key: Key): void {
    const s = SETTINGS[key];
    const rest = !s;
    if (rest !== this.resting) {
      this.resting = rest;
      const t = this.ctx.currentTime;
      this.bus.out.gain.cancelScheduledValues(t);
      this.bus.out.gain.setValueAtTime(this.bus.out.gain.value, t);
      this.bus.out.gain.linearRampToValueAtTime(rest || !this.running ? 0 : LEVEL * this.arr.trim, t + 5);
    }
    if (s && s !== this.setting) this.pending = { ...this.pending, setting: s };
  }
  /** The scene's tags (instruments and feel), from the canon pass; null: the setting's own. Lands on the next chord. */
  setTags(tags: MusicTags | null): void {
    if (tags?.ensemble === this.tags?.ensemble && tags?.feel === this.tags?.feel) return;
    this.pending = { ...this.pending, tags };
  }
  setMood(m: Mood, now = false): void {
    this.target = intensityOf(m);
    if (now) this.e = this.target;
  }
  accent(): void { this.pendingAccent = true; }

  /** A chapter ends: the music thins out and fades into silence over seven seconds, then rests a few bars. */
  closeChapter(): void {
    if (!this.running) return;
    const t = this.ctx.currentTime, g = this.bus.out.gain;
    g.cancelScheduledValues(t); g.setValueAtTime(g.value, t); g.linearRampToValueAtTime(0, t + 7);
    this.held.forEach((h) => h.stop(t + 7)); this.held = []; this.phrase = null; this.guest = null;
    this.section = { kind: "silent", until: this.bar + 6 };
  }
  /** The story ends: a short coda on the piano falling home in the scene's key, left to ring, then silence. */
  coda(): void {
    const t = this.ctx.currentTime + 0.3, r = this.root + 12, g = this.bus.out.gain;
    this.running = false;
    this.held.forEach((h) => h.stop(t + 1)); this.held = []; this.phrase = null; this.guest = null;
    g.cancelScheduledValues(t); g.setValueAtTime(Math.max(g.value, 0.0001), t); g.linearRampToValueAtTime(LEVEL * 1.1, t + 0.4);
    g.setValueAtTime(LEVEL * 1.1, t + 4); g.linearRampToValueAtTime(0, t + 9);
    [[16, 0], [14, 0.6], [12, 1.2], [11, 1.8], [7, 2.5], [12, 3.4]].forEach(([d, at], i) => I.piano(this.ctx, this.bus, t + at!, r + d!, 0.32 - i * 0.02, -0.1 + i * 0.04));
    I.piano(this.ctx, this.bus, t + 3.4, r - 12, 0.26, 0);
  }

  private get bpm() { return clamp(54 + 30 * this.e + this.feel.tempo, 44, 104); }
  private get mode(): Mode { const m = this.feel.modes; return this.e < 0.38 ? m[0] : this.e < 0.72 ? m[1] : m[2]; }
  private get root() { return this.setting.root; }

  schedule(until: number): void {
    if (!this.running) return;
    while (this.t < until) {
      const sixteenth = 60 / this.bpm / 4;
      this.tick(this.t, sixteenth);
      this.t += sixteenth;
      this.step++;
    }
  }

  /** Applies a pending setting or tags change (on a chord change, so the music turns, never lurches). */
  private applyPending(): void {
    if (!this.pending) return;
    if (this.pending.setting) this.setting = this.pending.setting;
    if (this.pending.tags !== undefined) this.tags = this.pending.tags;
    this.pending = null;
    const next = ARR[this.tags?.ensemble ?? this.setting.ensemble];
    if (next !== this.arr) {
      void I.prefetch(this.ctx, samplesFor(this.tags?.ensemble ?? this.setting.ensemble));
      this.guest = null; this.phrase = null; this.melodyDeg = -1000;
      if (this.running && !this.resting && this.section.kind !== "silent") this.level(next === ARR.silence ? 0 : LEVEL * next.trim, 4);
    }
    this.arr = next;
    this.feel = FEELS[this.tags?.feel ?? this.setting.feel];
  }

  /** Moves the score's level to `v` over `secs`, from where it is now. */
  private level(v: number, secs: number): void {
    const g = this.bus.out.gain, t = this.t;
    g.cancelScheduledValues(t); g.setValueAtTime(g.value, t); g.linearRampToValueAtTime(v, t + secs);
  }
  private get silent() { return this.resting || this.arr === ARR.silence || this.section.kind === "silent"; }

  /** The next section: calm stretches breathe (sparse, or silent), building ones less, a peak or crisis never. */
  private nextSection(): void {
    const { rnd } = this;
    const e = this.e, rising = this.target > this.e + 0.12;
    const wasSilent = this.section.kind === "silent";
    let kind: "full" | "sparse" | "silent" = "full";
    if (this.sections && !rising && e < 0.72) {
      const silentP = wasSilent ? 0 : e < 0.38 ? 0.3 : 0.1;
      const sparseP = e < 0.38 ? 0.32 : 0.2;
      const r = rnd();
      kind = r < silentP ? "silent" : r < silentP + sparseP ? "sparse" : "full";
    }
    const bars = kind === "silent" ? 3 + Math.floor(rnd() * 4) : kind === "sparse" ? 4 + Math.floor(rnd() * 6) : 4 + Math.floor(rnd() * 7);
    if (kind === "silent" && !wasSilent) { this.level(0, 3.5); this.held.forEach((h) => h.stop(this.t + 2)); this.held = []; this.phrase = null; this.guest = null; }
    if (kind !== "silent" && wasSilent && !this.resting && this.arr !== ARR.silence) this.level(LEVEL * this.arr.trim * (kind === "sparse" ? 1.3 : 1), 4);
    else if (!wasSilent && kind !== "silent") this.level(LEVEL * this.arr.trim * (kind === "sparse" ? 1.3 : 1), 3);
    this.section = { kind, until: this.bar + bars };
  }

  private tick(at: number, sx: number): void {
    const { ctx, bus, rnd } = this;
    const s = this.step % 16;
    if (s === 0) {
      this.bar++;
      this.e += (this.target - this.e) * 0.22;
      bus.air.gain.setTargetAtTime(this.silent ? 0 : 0.003 + 0.04 * Math.max(0, this.e - 0.35) + 0.02 * this.feel.dark * this.e, at, 1.5);
      bus.delay.delayTime.setTargetAtTime(sx * 6, at, 1.5);
      // A peak or a crisis cuts a quiet section short.
      if (this.bar >= this.section.until || (this.section.kind !== "full" && (this.target >= 0.72 || this.target > this.e + 0.2))) this.nextSection();
      const every = this.e < 0.3 ? 4 : this.e < 0.75 ? 2 : 1;
      if (this.bar % every === 0 || !this.chord) this.changeChord(at, sx * 16 * every);
      // Now and then a guest joins for eight bars.
      if (this.bar % 8 === 4) this.guest = rnd() < 0.55 && this.arr.guests.length ? { kind: pick(this.arr.guests, rnd), until: this.bar + 8 } : null;
      if (this.guest && this.bar >= this.guest.until) this.guest = null;
    }
    if (this.pendingAccent && !this.resting) { this.pendingAccent = false; this.level(LEVEL * this.arr.trim, 0.2); I.stinger(ctx, bus, at, this.root); }
    if (this.silent || !this.chord) return;
    const jitter = () => at + (rnd() - 0.5) * 0.012;
    if (this.section.kind === "sparse") { this.alone(at, s, sx, jitter); return; }
    this.rhythm(at, s, jitter);
    this.melody(sx, jitter);
    this.guestPart(at, s, sx, jitter);
  }

  /**
   * A sparse section: one voice alone, quietly. Bowed and blown leads hold long notes that barely move (a near-drone),
   * the guitars strum one soft chord a bar, the struck ones place a single note now and then.
   */
  private alone(at: number, s: number, sx: number, jitter: () => number): void {
    const { ctx, bus, rnd } = this;
    const c = this.chord!, root = this.root, bar = 16 * sx;
    const h = this.arr.harmony, lead = this.arr.lead;
    if (h === "pick" || h === "strum") {
      if (s === 0) {
        const strings = [c.bass + 12, ...spread(root, c, c.bass + 19, c.bass + 31, true)].slice(0, 5);
        strings.forEach((m, i) => I.pluck(ctx, bus, at + i * 0.03, m, 0.09, "guitar", -0.2 + i * 0.1));
      }
      return;
    }
    if (h === "choir" || !lead) {
      if (s === 0 && this.bar % 2 === 0) this.held.push(I.choir(ctx, bus, at, c.tones[0]!, 0.035, 0, 3));
      return;
    }
    if (SUSTAINED.has(lead)) {
      // One long note a bar or two, a step at most from the last: a line that barely changes.
      if (s !== 0 || (this.bar % 2 === 1 && rnd() < 0.6)) return;
      const midi = this.melodyNote(weighted<number>([[0, 4], [-1, 1.5], [1, 1.5]], rnd), false, lead);
      const low = lead === "violin" ? midi - 12 : midi;
      const dur = bar * (rnd() < 0.5 ? 2 : 1) * 0.97;
      if (lead === "flute") I.flute(ctx, bus, jitter(), low, 0.06, dur, 0);
      else if (lead === "clarinet") I.clarinet(ctx, bus, jitter(), low, 0.06, dur, 0);
      else if (lead === "horn") I.horn(ctx, bus, jitter(), low, 0.05, dur, 0);
      else I.bowed(ctx, bus, jitter(), low, 0.07, dur, lead === "cello" ? "cello" : "violin", 0, { solo: this.arr.solo });
      return;
    }
    if (s % 8 === 0 && rnd() < 0.45) {
      const midi = this.melodyNote(weighted<number>([[0, 2], [-1, 2], [1, 2], [2, 1], [-2, 1]], rnd), rnd() < 0.3, lead);
      if (lead === "piano") I.piano(ctx, bus, jitter(), midi, 0.08, 0);
      else if (lead === "harp") I.pluck(ctx, bus, jitter(), midi, 0.25, "harp", 0);
      else if (lead === "vibes") I.mallet(ctx, bus, jitter(), midi, 0.07, "vibraphone", 0);
      else if (lead === "ep") I.ep(ctx, bus, jitter(), midi, 0.06, 0);
      else I.bell(ctx, bus, jitter(), midi, 0.07, lead === "musicbox" ? "musicbox" : "celesta", 0);
    }
  }

  private changeChord(at: number, len: number): void {
    const { ctx, bus, rnd } = this;
    this.applyPending();
    const mode = this.mode;
    const dark = this.e >= 0.72 || this.feel.dark > 0.5;
    const deg = this.chord ? nextDegree(mode, this.chord.deg, dark, rnd) : 0;
    const count = this.e < 0.3 ? 3 : 4;
    const next = voice(this.root, mode, deg, this.chord?.tones ?? [], count);
    this.held.forEach((h) => h.stop(at));
    this.held = [];
    this.chord = next;
    if (this.silent || this.section.kind === "sparse") return;
    const swell = clamp(len * 0.35, 1.2, 5);
    const n = next.tones.length;
    const panOf = (i: number) => (i / (n - 1 || 1) - 0.5) * 0.9;
    const tense = this.feel.dark > 0.4 || this.e > 0.7;
    switch (this.arr.harmony) {
      case "pad": {
        const cutoff = 380 + this.setting.bright * 900 + this.e * 1500;
        next.tones.forEach((m, i) => this.held.push(I.pad(ctx, bus, at + i * 0.04, m, 0.026 + 0.01 * (1 - i / n), panOf(i), cutoff, swell)));
        break;
      }
      case "strings":
        next.tones.forEach((m, i) => I.bowed(ctx, bus, at + i * 0.05, m, 0.045, len * 0.97, m < this.root + 19 ? "cello" : "viola", panOf(i), { tremolo: tense && this.e > 0.7 }));
        break;
      case "choir":
        next.tones.forEach((m, i) => this.held.push(I.choir(ctx, bus, at + i * 0.06, m, 0.05, panOf(i), swell * 1.2)));
        break;
      case "horns":
        next.tones.slice(0, 3).forEach((m, i) => I.horn(ctx, bus, at + i * 0.04, m - 12 >= this.root + 7 ? m - 12 : m, 0.05 + 0.03 * this.e, len * 0.9, panOf(i)));
        break;
      case "piano":
        // A soft broken chord as the harmony turns.
        if (rnd() < 0.8) next.tones.forEach((m, i) => I.piano(ctx, bus, at + 0.02 + i * (0.06 + rnd() * 0.05), m, 0.07, panOf(i) * 0.6));
        break;
      case "ep": {
        if (rnd() < 0.6) next.tones.forEach((m, i) => I.ep(ctx, bus, at + 0.02 + i * (0.05 + rnd() * 0.03), m + 12, 0.035, (rnd() - 0.5) * 0.5));
        const cutoff = 380 + this.setting.bright * 900 + this.e * 1200;
        next.tones.slice(0, 3).forEach((m, i) => this.held.push(I.pad(ctx, bus, at, m, 0.016, panOf(i), cutoff, swell)));
        break;
      }
      case "vibes":
        next.tones.forEach((m, i) => I.mallet(ctx, bus, at + i * 0.03, m + 12, 0.05, "vibraphone", panOf(i)));
        break;
      default: {
        // Rhythmic harmonies (pick, strum, harp, marimba, celesta) play in rhythm(); a quiet bed holds them together.
        const cutoff = 300 + this.setting.bright * 600 + this.e * 800;
        next.tones.slice(0, 2).forEach((m, i) => this.held.push(I.pad(ctx, bus, at, m, 0.012, panOf(i), cutoff, swell)));
      }
    }
    // The bass.
    if (this.arr.bass === "sub") this.held.push(I.sub(ctx, bus, at, next.bass, 0.15, null));
    else if (this.arr.bass === "cello") I.bowed(ctx, bus, at, next.bass + (next.bass < 40 ? 12 : 0), 0.06, len * 0.98, "cello", -0.2, { tremolo: tense && this.e > 0.75, solo: this.arr.solo });
    else if (this.arr.bass === "bass") I.pluck(ctx, bus, at, next.bass - (next.bass > 45 ? 12 : 0), 0.55, "bass", 0);
    // A far tubular bell when a dark, tense scene changes harmony.
    if (this.feel.dark > 0.5 && this.e > 0.5 && rnd() < 0.3) I.bell(ctx, bus, at + 0.1, next.bass + 12, 0.05, "tubular", (rnd() - 0.5) * 0.6);
  }

  /** The rhythm section: guitar picking or strumming, harp arpeggios, a marimba ostinato, the bass line, and a pulse. */
  private rhythm(at: number, s: number, jitter: () => number): void {
    const { ctx, bus, rnd } = this;
    const c = this.chord!, e = this.e, root = this.root;
    const h = this.arr.harmony;
    if (h === "pick") {
      // Travis picking in eighths: thumb on the bass, fingers on the chord above; strummed when it builds.
      if (e > 0.6) this.strum(at, s);
      else if (s % 2 === 0) {
        const tones = spread(root, c, root + 12, root + 31);
        const thumb = s % 8 === 0 ? c.bass + 12 : s % 8 === 4 ? c.bass + 19 : null;
        const note = thumb ?? tones[[2, 1, 3, 1][(s / 2) % 4]! % tones.length]!;
        if (thumb !== null || rnd() < 0.55 + e * 0.5) I.pluck(ctx, bus, jitter(), note, (thumb !== null ? 0.4 : 0.28) * (0.85 + rnd() * 0.3), "guitar", thumb !== null ? -0.15 : 0.2);
      }
    } else if (h === "strum") {
      this.strum(at, s);
    } else if (h === "harp") {
      const grid = e < 0.35 ? 4 : e < 0.72 ? 2 : 1;
      if (s % grid === 0 && rnd() < 0.75 + e * 0.25) {
        const tones = spread(root, c, root + 12, root + 36);
        const k = Math.floor(s / grid) % tones.length;
        I.pluck(ctx, bus, jitter(), tones[k]!, 0.3 * (0.8 + rnd() * 0.3), "harp", (k / tones.length - 0.5) * 0.8);
      }
    } else if (h === "marimba") {
      if (s % 2 === 0 && rnd() < 0.55 + e * 0.45) {
        const tones = [c.bass + 12, c.bass + 19, c.bass + 24, c.tones[0]! + 12];
        I.mallet(ctx, bus, jitter(), tones[(s / 2) % 4]!, 0.12 * (0.8 + rnd() * 0.3), "marimba", ((s / 2) % 4 - 1.5) * 0.25);
      }
    } else if (h === "celesta") {
      if (s % 4 === 2 && rnd() < 0.3 + e * 0.4) I.bell(ctx, bus, jitter(), pick(spread(root, c, root + 36, root + 48), rnd), 0.06, "celesta", (rnd() - 0.5) * 0.8);
    }
    // The bass line of the plucked ensembles: the root on one, the fifth on three as it builds, walking at a peak.
    if (this.arr.bass === "bass" && s !== 0) {
      const low = c.bass - (c.bass > 45 ? 12 : 0);
      if (s === 8 && e > 0.3) I.pluck(ctx, bus, jitter(), low + 7, 0.42, "bass", 0);
      if (e > 0.72 && s % 4 === 2 && rnd() < 0.6) I.pluck(ctx, bus, jitter(), low + pick([0, 7, 12, 10], rnd), 0.35, "bass", 0);
    }
    // A pulse for settings with a rhythm of their own (the train, the clock) or when the story tightens.
    const pulse = clamp(this.setting.pulse + (e - 0.45) * 0.9, 0, 1);
    if (this.arr.pulse && pulse > 0.15 && s % 2 === 0 && rnd() < pulse * (s % 8 === 0 ? 1 : 0.7)) {
      const tone = s % 8 === 0 ? c.bass + 12 : pick([c.bass + 12, c.bass + 19, c.tones[0]!], rnd);
      const v = (0.05 + 0.06 * pulse) * (s % 8 === 0 ? 1 : 0.7);
      const p = this.arr.pulse;
      if (p === "pizz") I.pluck(ctx, bus, jitter(), tone, v * 4, "pizz", (rnd() - 0.5) * 0.4);
      else if (p === "marimba") I.mallet(ctx, bus, jitter(), tone + 12, v * 1.6, "marimba", (rnd() - 0.5) * 0.4);
      else if (p === "glock") { if (s % 4 === 0) I.bell(ctx, bus, jitter(), tone + 36, v * 0.6, "glockenspiel", (rnd() - 0.5) * 0.6); }
      else if (p === "piano") I.piano(ctx, bus, jitter(), tone, v * 1.2, (rnd() - 0.5) * 0.3);
      else I.ep(ctx, bus, jitter(), tone, v, (rnd() - 0.5) * 0.4);
    }
  }

  /** A guitar strum: down strokes low to high, lighter up strokes high to low, in a pattern that tightens with tension. */
  private strum(at: number, s: number): void {
    const { ctx, bus, rnd } = this;
    const c = this.chord!, e = this.e;
    const calm = [0, 8], folk = [0, 4, 6, 10, 12, 14], drive = [0, 2, 4, 6, 8, 10, 12, 14];
    const pattern = e < 0.35 ? calm : e < 0.72 ? folk : drive;
    if (!pattern.includes(s)) return;
    const down = s % 4 === 0;
    const strings = [c.bass + 12, ...spread(this.root, c, c.bass + 19, c.bass + 36, true)].slice(0, 6);
    const order = down ? strings : strings.slice(-4).reverse();
    const gap = 0.012 + rnd() * 0.008;
    const v = (down ? 0.22 : 0.13) * (0.85 + rnd() * 0.3) * (0.8 + e * 0.4);
    order.forEach((m, i) => I.pluck(ctx, bus, at + i * gap, m, v, "guitar", -0.3 + (i / order.length) * 0.6));
  }

  /** The melody: phrases with rests, in the lead's own register; held notes for bowed and blown leads. */
  private melody(sx: number, jitter: () => number): void {
    const lead = this.arr.lead;
    if (!lead) return;
    const { ctx, bus, rnd } = this;
    const e = this.e;
    if (!this.phrase && this.step % 8 === 0 && rnd() < (SUSTAINED.has(lead) ? 0.14 : 0.1) + 0.36 * e) this.startPhrase(lead);
    const ph = this.phrase;
    if (!ph) return;
    const n = ph.notes[ph.i];
    if (!n || n.at > this.step) return;
    const last = ph.i === ph.notes.length - 1;
    const midi = this.melodyNote(n.d, last, lead);
    const vel = (0.06 + 0.07 * e) * (ph.i === 0 ? 1.1 : 0.85 + rnd() * 0.25);
    const dur = n.len * sx * this.feel.legato * (last ? 2.2 : 1);
    const pan = (rnd() - 0.5) * 0.5;
    switch (lead) {
      case "cello": I.bowed(ctx, bus, jitter(), midi, vel * 1.25, dur, "cello", pan, { solo: this.arr.solo }); break;
      case "violin": I.bowed(ctx, bus, jitter(), midi, vel * 1.1, dur, "violin", pan, { solo: this.arr.solo }); break;
      case "flute": I.flute(ctx, bus, jitter(), midi, vel * 1.4, dur, pan); break;
      case "clarinet": I.clarinet(ctx, bus, jitter(), midi, vel * 1.2, dur, pan); break;
      case "horn": I.horn(ctx, bus, jitter(), midi, vel * 1.3, dur, pan); break;
      case "harp": I.pluck(ctx, bus, jitter(), midi, vel * 4, "harp", pan); break;
      case "celesta": I.bell(ctx, bus, jitter(), midi, vel * 1.2, "celesta", pan); break;
      case "musicbox": I.bell(ctx, bus, jitter(), midi, vel * 1.1, "musicbox", pan); break;
      case "vibes": I.mallet(ctx, bus, jitter(), midi, vel * 1.3, "vibraphone", pan); break;
      case "piano": I.piano(ctx, bus, jitter(), midi, vel * 1.6, pan); break;
      default: I.ep(ctx, bus, jitter(), midi, vel, pan);
    }
    ph.i++;
    if (ph.i >= ph.notes.length) this.phrase = null;
  }

  private startPhrase(lead: Lead): void {
    const { rnd } = this;
    const e = this.e;
    // Bowed and blown leads breathe in longer notes; struck ones move more.
    const slow = SUSTAINED.has(lead);
    const grid = e < 0.38 ? (slow ? 8 : 4) : e < 0.72 ? (slow ? 4 : 2) : pick(slow ? [2, 4] : [1, 2], rnd);
    const len = 3 + Math.floor(rnd() * (2 + 4 * e));
    const notes: { at: number; d: number; len: number }[] = [];
    let pos = this.step + 1;
    for (let i = 0; i < len; i++) {
      const g = grid * (rnd() < 0.25 ? 2 : 1);
      notes.push({ at: pos, d: i === 0 ? 0 : weighted<number>([[-1, 3], [1, 3], [-2, 1.2], [2, 1.2], [0, 0.5], [3, 0.4], [-3, 0.4]], rnd), len: g });
      pos += g;
    }
    this.phrase = { notes, i: 0 };
  }

  /** The next melody pitch: a step from the last one in the mode, near the lead's centre, ending on a chord tone. */
  private melodyNote(d: number, last: boolean, lead: Lead): number {
    const mode = MODES[this.mode], root = this.root;
    const at = (deg: number) => root + mode[((deg % 7) + 7) % 7]! + 12 * Math.floor(deg / 7);
    // Degrees spanning the lead's register: from a fifth below its centre to about an octave and a half above that.
    let lo = 0;
    while (at(lo) < CENTRE[lead] - 7) lo++;
    while (at(lo) > CENTRE[lead] - 7) lo--;
    if (this.melodyDeg < lo || this.melodyDeg > lo + 11) this.melodyDeg = lo + 4;
    this.melodyDeg = clamp(this.melodyDeg + d, lo, lo + 11);
    if (last && this.chord) {
      const pcs = this.chord.pcs;
      for (let k = 0; k < 4; k++) for (const dd of [k, -k]) {
        const m = at(this.melodyDeg + dd);
        if (pcs.includes(((m - root) % 12 + 12) % 12)) { this.melodyDeg += dd; return m; }
      }
    }
    return at(this.melodyDeg);
  }

  /** The guest of the moment: a counter-line, sparkles, a glissando, a far bell, a choir. */
  private guestPart(at: number, s: number, sx: number, jitter: () => number): void {
    const g = this.guest;
    if (!g) return;
    const { ctx, bus, rnd } = this;
    const c = this.chord!, root = this.root, e = this.e;
    const bar = 16 * sx;
    switch (g.kind) {
      case "flute": case "clarinet": case "violin": case "cello": {
        // A slow counter-line: a chord tone every half bar, sometimes held for the whole bar.
        if (s % 8 !== 0 || rnd() < 0.35) return;
        const reg = { flute: [74, 88], clarinet: [58, 70], violin: [70, 84], cello: [48, 60] }[g.kind];
        const note = pick(spread(root, c, reg[0]!, reg[1]!), rnd);
        const dur = bar * (rnd() < 0.4 ? 1 : 0.5) * this.feel.legato;
        const v = 0.05 + 0.03 * e;
        if (g.kind === "flute") I.flute(ctx, bus, jitter(), note, v * 1.3, dur, 0.4);
        else if (g.kind === "clarinet") I.clarinet(ctx, bus, jitter(), note, v * 1.1, dur, -0.4);
        else I.bowed(ctx, bus, jitter(), note, v * 1.1, dur, g.kind, g.kind === "violin" ? 0.4 : -0.4, { solo: this.arr.solo });
        return;
      }
      case "glock": case "celesta": case "handbell":
        if (s % 4 === 2 && rnd() < 0.22 + e * 0.2) {
          const reg = g.kind === "handbell" ? [64, 79] : g.kind === "celesta" ? [76, 91] : [84, 98];
          I.bell(ctx, bus, jitter(), pick(spread(root, c, reg[0]!, reg[1]!), rnd), g.kind === "glock" ? 0.035 : 0.05, g.kind === "glock" ? "glockenspiel" : g.kind, (rnd() - 0.5) * 0.9);
        }
        return;
      case "vibes":
        if (s % 8 === 4 && rnd() < 0.4) I.mallet(ctx, bus, jitter(), pick(spread(root, c, 67, 79), rnd), 0.05, "vibraphone", 0.3);
        return;
      case "harp":
        // A glissando up the chord as a bar begins.
        if (s === 0 && rnd() < 0.5) spread(root, c, root + 12, root + 40).forEach((m, i) => I.pluck(ctx, bus, at + i * 0.045, m, 0.18, "harp", -0.4 + i * 0.06));
        return;
      case "tubular":
        if (s === 0 && rnd() < 0.3) I.bell(ctx, bus, at, c.bass + 12, 0.05, "tubular", (rnd() - 0.5) * 0.6);
        return;
      case "choir":
        if (s === 0 && this.bar % 2 === 0) c.tones.slice(0, 3).forEach((m, i) => this.held.push(I.choir(ctx, bus, at + i * 0.08, m, 0.03, (i - 1) * 0.4, 2.5)));
        return;
    }
  }
}
